import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Card, Row, Col, Spinner } from 'react-bootstrap';
import api from '../../api/axios';
import { brandingDefaults } from '../../brand/brandSystem';
import { useBranding } from '../../contexts/BrandingContext';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminBranding = () => {
  const { refreshBranding } = useBranding();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const [settings, setSettings] = useState({
    ...brandingDefaults,
    logo_url: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const resolveBrandingUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) return rawUrl;
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;

    const base = (api?.defaults?.baseURL || '').replace(/\/$/, '');
    if (!base) return rawUrl;

    if (rawUrl.startsWith('/api/') || rawUrl.startsWith('/uploads/')) {
      return `${base}${rawUrl}`;
    }

    if (rawUrl.startsWith('api/') || rawUrl.startsWith('uploads/')) {
      return `${base}/${rawUrl}`;
    }

    return rawUrl;
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/admin/branding');
      setSettings(response.data);
      if (response.data.logo_url) {
        setLogoPreview(resolveBrandingUrl(response.data.logo_url));
      }
      setLoading(false);
    } catch (err) {
      console.error('Fetch settings error:', err);
      setError('Failed to load branding settings');
      setLoading(false);
    }
  };

  const handleColorChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSaveColors = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await api.put('/api/admin/branding', settings);
      await refreshBranding(); // Refresh branding across the app
      setSuccess('Colors saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save colors error:', err);
      setError('Failed to save colors');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await api.post('/api/admin/branding/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings({ ...settings, logo_url: response.data.logo_url });
      setLogoPreview(resolveBrandingUrl(response.data.logo_url));
      setLogoFile(null);
      await refreshBranding(); // Refresh branding across the app
      setSuccess('Logo uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Upload logo error:', err);
      setError('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the logo?')) return;

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      await api.delete('/api/admin/branding/logo');

      setSettings({ ...settings, logo_url: '' });
      setLogoPreview('');
      await refreshBranding(); // Refresh branding across the app
      setSuccess('Logo deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete logo error:', err);
      setError('Failed to delete logo');
    } finally {
      setUploading(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset all colors to default values?')) return;
    
    setSettings({
      ...settings,
      ...brandingDefaults,
      logo_url: settings.logo_url
    });
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">{t('admin.brandingSettings')}</h1>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Logo Section */}
      <Card className="mb-4">
        <Card.Header as="h5">{t('admin.logo')}</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Upload Logo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  Recommended: PNG or SVG format, max 2MB
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleUploadLogo}
                  disabled={!logoFile || uploading}
                >
                  {uploading ? t('common.loading') : t('admin.uploadLogo')}
                </Button>

                {settings.logo_url && (
                  <Button
                    variant="danger"
                    onClick={handleDeleteLogo}
                    disabled={uploading}
                  >
                    {t('admin.deleteLogo')}
                  </Button>
                )}
              </div>
            </Col>

            <Col md={6}>
              {logoPreview && (
                <div>
                  <p className="mb-2"><strong>Preview:</strong></p>
                  <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                    />
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Color Palette Section */}
      <Card className="mb-4">
        <Card.Header as="h5">{t('admin.colorPalette')}</Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-4">
            <strong>🎨 Complete Color Control:</strong>
            <ul className="mb-0 mt-2">
              <li><strong>Primary Color</strong> → Buttons, links, and accent elements (--brand-accent)</li>
              <li><strong>Text Color</strong> → Main text, navbar, and headings (--brand-ink)</li>
              <li><strong>Background Color</strong> → Overall page background (--brand-cream)</li>
              <li><strong>Success Color</strong> → Stock indicators and confirmations (--brand-leaf)</li>
              <li><strong>Secondary, Danger, Warning, Info</strong> → Alert messages and UI states</li>
              <li>✨ The system automatically generates lighter and darker variations</li>
              <li>💡 All custom CSS variables update instantly when you save</li>
            </ul>
          </Alert>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Primary Color (Brand Accent)</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.primary_color}
                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Used for buttons, links, and primary actions
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Secondary Color</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.secondary_color}
                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.secondary_color}
                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Used for secondary buttons and neutral elements
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Success Color (In Stock, Confirmations)</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.success_color}
                    onChange={(e) => handleColorChange('success_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.success_color}
                    onChange={(e) => handleColorChange('success_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Used for success messages and stock indicators
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Danger Color</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.danger_color}
                    onChange={(e) => handleColorChange('danger_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.danger_color}
                    onChange={(e) => handleColorChange('danger_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Used for error messages and delete actions
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Warning Color</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.warning_color}
                    onChange={(e) => handleColorChange('warning_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.warning_color}
                    onChange={(e) => handleColorChange('warning_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Used for warnings and low stock alerts
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Info Color</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.info_color}
                    onChange={(e) => handleColorChange('info_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.info_color}
                    onChange={(e) => handleColorChange('info_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Used for informational messages and highlights
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Background Color</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.background_color}
                    onChange={(e) => handleColorChange('background_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.background_color}
                    onChange={(e) => handleColorChange('background_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Main background color of the application
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Text Color (Main Dark Color)</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    value={settings.text_color}
                    onChange={(e) => handleColorChange('text_color', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <Form.Control
                    type="text"
                    value={settings.text_color}
                    onChange={(e) => handleColorChange('text_color', e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Main text color, navbar background, and headings
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button
              variant="primary"
              onClick={handleSaveColors}
              disabled={saving}
            >
              {saving ? t('common.loading') : t('admin.saveColors')}
            </Button>
            
            <Button
              variant="outline-secondary"
              onClick={resetToDefaults}
            >
              {t('admin.resetToDefaults')}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Color Preview Section */}
      <Card>
        <Card.Header as="h5">{t('admin.colorPreview')}</Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">Preview how the colors will look in the application:</p>
          
          <div className="d-flex flex-wrap gap-3">
            <Button style={{ backgroundColor: settings.primary_color, borderColor: settings.primary_color }}>
              Primary Button
            </Button>
            <Button style={{ backgroundColor: settings.secondary_color, borderColor: settings.secondary_color }}>
              Secondary Button
            </Button>
            <Button style={{ backgroundColor: settings.success_color, borderColor: settings.success_color }}>
              Success Button
            </Button>
            <Button style={{ backgroundColor: settings.danger_color, borderColor: settings.danger_color }}>
              Danger Button
            </Button>
            <Button style={{ backgroundColor: settings.warning_color, borderColor: settings.warning_color, color: '#000' }}>
              Warning Button
            </Button>
            <Button style={{ backgroundColor: settings.info_color, borderColor: settings.info_color }}>
              Info Button
            </Button>
          </div>

          <div className="mt-4 p-3 rounded" style={{ backgroundColor: settings.background_color, color: settings.text_color, border: '1px solid #dee2e6' }}>
            <p className="mb-0">This is sample text showing how content will appear with your selected background and text colors.</p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminBranding;
